/**
 * Bulk-update properties — paste TSV/CSV (e.g. from Excel) to update many at once.
 * Workflow: Download template → Edit in Excel → Paste back → Preview → Apply.
 */
import { useMemo, useState } from "react";
import { ClipboardPaste, FileDown, CheckCircle2, AlertCircle, Save } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";

const FIELDS = ["code", "name", "address", "type", "bedrooms", "bathrooms", "capacity", "state", "notes"];
const NUMERIC = new Set(["bedrooms", "bathrooms", "capacity"]);

function splitLine(line, delim) {
    // Very simple CSV parser: supports quoted fields with delim inside.
    const out = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') {
            if (inQ && line[i + 1] === '"') {
                cur += '"';
                i++;
            } else {
                inQ = !inQ;
            }
        } else if (c === delim && !inQ) {
            out.push(cur);
            cur = "";
        } else {
            cur += c;
        }
    }
    out.push(cur);
    return out.map((s) => s.trim());
}

function parsePasted(text) {
    if (!text.trim()) return { rows: [], headers: [], errors: [] };
    const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
    const firstLine = lines[0];
    const delim = firstLine.includes("\t") ? "\t" : ",";
    const headerCols = splitLine(firstLine, delim).map((h) => h.toLowerCase().trim());
    const errors = [];
    const recognizedHeaders = headerCols.map((h) => (FIELDS.includes(h) ? h : null));
    if (!recognizedHeaders.includes("code")) {
        errors.push("La primera fila debe contener una columna 'code' (puedes obtenerla con 'Descargar plantilla').");
    }
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
        const cells = splitLine(lines[i], delim);
        const row = { _line: i + 1 };
        for (let c = 0; c < recognizedHeaders.length; c++) {
            const field = recognizedHeaders[c];
            const val = cells[c];
            if (!field || val === undefined || val === "") continue;
            if (NUMERIC.has(field)) {
                const n = parseInt(val, 10);
                if (!isNaN(n)) row[field] = n;
            } else {
                row[field] = val;
            }
        }
        if (row.code) rows.push(row);
    }
    return { rows, headers: recognizedHeaders, errors };
}

export default function BulkUpdateModal({ open, onClose, onUpdated }) {
    const [text, setText] = useState("");
    const [result, setResult] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const parsed = useMemo(() => parsePasted(text), [text]);

    const close = () => {
        setText("");
        setResult(null);
        setError("");
        setSaving(false);
        onClose?.();
    };

    const apply = async () => {
        if (parsed.rows.length === 0) {
            setError("Sin filas válidas para aplicar");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const payloadRows = parsed.rows.map(({ _line, ...rest }) => rest);
            const { data } = await api.post("/properties/bulk-update", { rows: payloadRows });
            setResult(data);
            onUpdated?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const downloadTemplate = async () => {
        // Use direct fetch with token to grab the CSV
        const token = localStorage.getItem("zentra_token");
        const res = await fetch(`${API}/exports/properties.csv`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
            setError("No se pudo descargar la plantilla");
            return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "zentra-propiedades.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Modal
            open={open}
            onClose={close}
            title="Actualizar propiedades en masa"
            subtitle="Pega contenido tabular (TSV de Excel o CSV). El sistema busca por 'code' y solo actualiza los campos que rellenes."
            wide
            testId="bulk-update-modal"
            footer={
                result ? (
                    <button className="zentra-btn-primary" onClick={close} data-testid="bulk-update-done">
                        Cerrar
                    </button>
                ) : (
                    <>
                        <button className="zentra-btn-ghost" onClick={close} disabled={saving}>
                            Cancelar
                        </button>
                        <button
                            className="zentra-btn-accent"
                            onClick={apply}
                            disabled={saving || parsed.rows.length === 0 || parsed.errors.length > 0}
                            data-testid="bulk-update-apply"
                        >
                            <Save size={14} />
                            {saving ? "Aplicando…" : `Aplicar a ${parsed.rows.length} propiedades`}
                        </button>
                    </>
                )
            }
        >
            {!result ? (
                <div className="space-y-4">
                    {/* Step 1 instructions */}
                    <div className="rounded-md bg-zentra-bg/50 border border-zentra-border p-4 text-sm space-y-2">
                        <p className="font-display font-semibold text-zentra-ink">Cómo funciona</p>
                        <ol className="list-decimal pl-5 text-zentra-muted space-y-1">
                            <li>
                                Descarga la <strong>plantilla</strong> con tus propiedades actuales (ya rellena con
                                códigos AVA-001, AVA-002, etc.).
                            </li>
                            <li>Ábrela en Excel / Google Sheets y completa las columnas (dirección, habitaciones…).</li>
                            <li>
                                Vuelve aquí: <strong>selecciona todo (Ctrl/Cmd+A) y copia (Ctrl/Cmd+C)</strong>, después
                                pega abajo.
                            </li>
                            <li>Revisa la previsualización y aplica.</li>
                        </ol>
                        <button
                            type="button"
                            onClick={downloadTemplate}
                            className="zentra-btn-ghost text-xs mt-2"
                            data-testid="bulk-download-template"
                        >
                            <FileDown size={13} /> Descargar plantilla CSV
                        </button>
                    </div>

                    {/* Paste area */}
                    <div>
                        <label className="zentra-label">
                            <ClipboardPaste size={11} className="inline mr-1 -mt-0.5" />
                            Pega aquí (TSV desde Excel o CSV)
                        </label>
                        <textarea
                            className="zentra-textarea font-mono-zentra text-xs min-h-[180px]"
                            placeholder={"code\tname\taddress\tbedrooms\tbathrooms\tcapacity\nAVA-001\tHuelin\tC/ Salvador Espada 12\t2\t1\t4\n..."}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            data-testid="bulk-update-textarea"
                        />
                    </div>

                    {/* Detected headers + parse errors */}
                    {text.trim() && (
                        <div className="rounded-md border border-zentra-border bg-white p-3 text-xs">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                                <span className="text-[10px] uppercase tracking-widest text-zentra-muted font-semibold">
                                    Columnas detectadas:
                                </span>
                                {parsed.headers.map((h, i) => (
                                    <span
                                        key={i}
                                        className={`zentra-chip ${
                                            h
                                                ? "bg-emerald-100 text-emerald-900 border-emerald-200"
                                                : "bg-red-100 text-red-800 border-red-200"
                                        }`}
                                    >
                                        {h || `(col ${i + 1} ignorada)`}
                                    </span>
                                ))}
                            </div>
                            {parsed.errors.length > 0 ? (
                                <p className="text-red-700">{parsed.errors.join(" · ")}</p>
                            ) : (
                                <p className="text-zentra-muted">
                                    {parsed.rows.length} filas listas para aplicar.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Preview */}
                    {parsed.rows.length > 0 && parsed.errors.length === 0 && (
                        <div className="rounded-md border border-zentra-border bg-white overflow-hidden">
                            <div className="px-3 py-2 bg-zentra-bg/60 border-b border-zentra-border">
                                <span className="text-[10px] tracking-widest uppercase font-semibold text-zentra-muted">
                                    Previsualización (primeras 5)
                                </span>
                            </div>
                            <div className="overflow-x-auto max-h-[200px]">
                                <table className="w-full text-xs">
                                    <thead className="bg-zentra-bg/40">
                                        <tr>
                                            {parsed.headers
                                                .filter(Boolean)
                                                .map((h) => (
                                                    <th
                                                        key={h}
                                                        className="text-left px-3 py-1.5 font-semibold text-zentra-muted uppercase tracking-wider text-[10px]"
                                                    >
                                                        {h}
                                                    </th>
                                                ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parsed.rows.slice(0, 5).map((row, i) => (
                                            <tr key={i} className="border-t border-zentra-border">
                                                {parsed.headers
                                                    .filter(Boolean)
                                                    .map((h) => (
                                                        <td key={h} className="px-3 py-1.5 text-zentra-ink">
                                                            {row[h] ?? <span className="text-zentra-muted">—</span>}
                                                        </td>
                                                    ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                            {error}
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-3" data-testid="bulk-update-result">
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 size={18} className="text-emerald-700" />
                            <p className="font-display font-semibold text-emerald-900">
                                {result.updated} propiedades actualizadas correctamente
                            </p>
                        </div>
                        <p className="text-xs text-emerald-800 mt-2">
                            {result.total} filas procesadas · {result.not_found.length} no encontradas ·{" "}
                            {result.errors.length} errores
                        </p>
                    </div>
                    {result.not_found.length > 0 && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                            <p className="text-xs font-semibold text-amber-900 mb-1">
                                <AlertCircle size={11} className="inline mr-1" />
                                Códigos no encontrados (revisa que existan):
                            </p>
                            <p className="text-xs text-amber-800 font-mono-zentra">{result.not_found.join(", ")}</p>
                        </div>
                    )}
                    {result.errors.length > 0 && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-3">
                            <p className="text-xs font-semibold text-red-900 mb-1">Errores:</p>
                            <ul className="text-xs text-red-800 space-y-0.5">
                                {result.errors.map((e, i) => (
                                    <li key={i}>
                                        <strong>{e.code}</strong>: {e.error}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
}
